import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../ConfirmModal';
import { MonoLabel, SectionCap, EditorialButton, EmptyState } from '../Editorial';
import { beaconOpened, beaconRowOnce } from '../../lib/recruitingBeacons';
import CollegePicker from '../CollegePicker';

/* Offers half of the old OffersPage. Self-contained: own fetch, own state, own
   confirm modal and toast, no props. Behaviour is unchanged from the page it
   came from — including the engagement beacons and the 10-offer cap message. */

const OFFER_TYPES = [
  'Full Scholarship',
  'Partial Scholarship',
  'Preferred Walk-on',
  'Walk-on',
];

// NOTE: `sport` is seeded to match the AthleteOffer column default
// (backend/app/models/models.py — sport = Column(String, default="Football")).
// It is a legacy schema default every client inherits, not a web choice; see
// docs/RECRUIT-PARITY-AUDIT-2026-08-10.md F7. Fix the column and this together.
const emptyOffer = (slot) => ({
  slot_number: slot,
  college_name: '',
  college_city: '',
  college_state: '',
  sport: 'Football',
  offer_type: '',
  offer_date: '',
  amount: '',
  notes: '',
});

export default function OffersPanel() {
  const { API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [offers, setOffers] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  // Rows already beaconed this mount, so editing one row repeatedly counts once.
  const beaconedRows = useRef(new Set());

  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const toast = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const res = await axios.get(`${API_URL}/athletes/offers`, getAxiosConfig());
      const data = res.data || [];

      // Always show at least 1 empty form
      setOffers(data.length === 0 ? [emptyOffer(1)] : data);
    } catch (err) {
      console.error('Error fetching offers:', err);
    }
  };

  const saveOffer = async (offer) => {
    try {
      await axios.post(`${API_URL}/athletes/offers`, offer, getAxiosConfig());
      toast('Saved');
    } catch (err) {
      console.error('Error saving offer:', err);
    }
  };

  // Commit to (or un-commit from) a school. An athlete commits to one school at a
  // time, so checking one clears the rest; the backend enforces the same rule.
  const setCommitted = (offer, checked) => {
    if (!offer.college_name) return; // can't commit to an empty offer
    setOffers(prev => prev.map(o => ({
      ...o,
      committed: o.slot_number === offer.slot_number ? checked : (checked ? false : o.committed),
    })));
    saveOffer({ ...offer, committed: checked });
  };

  const handleAddOffer = () => {
    const usedSlots = offers.map(o => o.slot_number);
    let nextSlot = 1;
    while (usedSlots.includes(nextSlot) && nextSlot <= 10) nextSlot++;
    // Say so. This used to return silently, so Add just stopped working with
    // nothing explaining why. Same wording as OffersViewModel on both apps.
    if (nextSlot > 10) {
      toast("You've reached the 10-offer maximum.", 'error');
      return;
    }

    beaconOpened(API_URL, 'offers');

    setOffers([...offers, emptyOffer(nextSlot)]);
  };

  const handleDeleteOffer = (offer) => {
    setConfirmAction({
      title: 'Delete Offer',
      message: `Remove the offer from ${offer.college_name || 'this college'}?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        if (offer.id) {
          try {
            await axios.delete(`${API_URL}/athletes/offers/${offer.id}`, getAxiosConfig());
          } catch (err) {
            console.error('Error deleting offer:', err);
          }
        }
        setOffers(prev => prev.filter(o => o.slot_number !== offer.slot_number));
        toast('Offer deleted');
      },
      onCancel: () => setConfirmAction(null),
    });
  };

  const updateOfferField = (slot, field, value) => {
    beaconRowOnce(beaconedRows, API_URL, 'offers', slot);
    setOffers(prev => prev.map(o =>
      o.slot_number === slot ? { ...o, [field]: value } : o
    ));
  };

  const handleFieldBlur = (offer) => {
    if (!offer.college_name) return;
    saveOffer(offer);
  };

  // Writes the picked school into this offer row and saves it.
  const pickCollege = (slot) => (college) => {
    updateOfferField(slot, 'unitid', college.u);
    updateOfferField(slot, 'college_name', college.n);
    updateOfferField(slot, 'college_city', college.c);
    updateOfferField(slot, 'college_state', college.s);

    const offer = offers.find(o => o.slot_number === slot);
    if (offer) {
      saveOffer({
        ...offer,
        unitid: college.u,
        college_name: college.n,
        college_city: college.c,
        college_state: college.s,
      });
    }
  };

  // Typing into the college box replaces any earlier pick, so the old pick's id
  // and location are dropped with the first keystroke. Nothing is saved yet.
  const typeCollege = (slot, text) => setOffers(prev => prev.map(o => (o.slot_number === slot
    ? { ...o, college_name: text, unitid: null, college_city: '', college_state: '' } : o)));

  // Free text, once, when the box is left. An explicit `unitid: null` is what
  // tells the server to drop the id (an absent key means "keep", for old clients).
  const commitTypedName = (offer) => (text) => {
    if (text) saveOffer({ ...offer, college_name: text, unitid: null, college_city: '', college_state: '' });
  };

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      <SectionCap label="Offers" hint="MAX 10" />

      {offers.map((offer) => (
        <div className="ed-card" key={offer.slot_number}>
          <div className="ed-row ed-row--between" style={{ marginBottom: 8 }}>
            <MonoLabel>OFFER {offer.slot_number}</MonoLabel>
            <EditorialButton variant="ghost" onClick={() => handleDeleteOffer(offer)} style={{ color: 'var(--ed-accent-red)', padding: '6px 12px' }}>
              Delete
            </EditorialButton>
          </div>

          <div className="form-field" style={{ position: 'relative' }}>
            <label className="form-field__label">College / University</label>
            <CollegePicker
              value={offer.college_name}
              onInputChange={(text) => typeCollege(offer.slot_number, text)}
              onSelect={pickCollege(offer.slot_number)}
              onCommitText={commitTypedName(offer)}
            />
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="form-field__label">City</label>
              <input
                type="text"
                value={offer.college_city || ''}
                onChange={(e) => updateOfferField(offer.slot_number, 'college_city', e.target.value)}
                onBlur={() => handleFieldBlur(offer)}
                className="form-field__input"
                readOnly
              />
            </div>
            <div className="form-field">
              <label className="form-field__label">State</label>
              <input
                type="text"
                value={offer.college_state || ''}
                onChange={(e) => updateOfferField(offer.slot_number, 'college_state', e.target.value)}
                onBlur={() => handleFieldBlur(offer)}
                className="form-field__input"
                readOnly
              />
            </div>
          </div>

          <div className="form-field">
            <label className="form-field__label">Offer Type</label>
            <select
              value={offer.offer_type || ''}
              onChange={(e) => {
                updateOfferField(offer.slot_number, 'offer_type', e.target.value);
                const updated = { ...offer, offer_type: e.target.value };
                if (updated.college_name) saveOffer(updated);
              }}
              className="form-field__select"
            >
              <option value="">Select...</option>
              {OFFER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="form-field">
            <label className="form-field__checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: offer.college_name ? 'pointer' : 'not-allowed' }}>
              <input
                type="checkbox"
                checked={!!offer.committed}
                disabled={!offer.college_name}
                onChange={(e) => setCommitted(offer, e.target.checked)}
              />
              <span>Committed to this school</span>
            </label>
            <div className="form-field__hint">You can be committed to only one school at a time.</div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="form-field__label">Offer Date</label>
              <input
                type="date"
                value={offer.offer_date || ''}
                onChange={(e) => {
                  updateOfferField(offer.slot_number, 'offer_date', e.target.value);
                  if (offer.college_name) saveOffer({ ...offer, offer_date: e.target.value });
                }}
                className="form-field__input"
              />
            </div>
            <div className="form-field">
              <label className="form-field__label">Scholarship amount (optional)</label>
              <input
                type="text"
                value={offer.amount || ''}
                onChange={(e) => updateOfferField(offer.slot_number, 'amount', e.target.value)}
                onBlur={() => handleFieldBlur(offer)}
                className="form-field__input"
                placeholder="e.g. $50,000 / year"
              />
            </div>
          </div>

          <div className="form-field">
            <label className="form-field__label">Notes</label>
            <textarea
              value={offer.notes || ''}
              onChange={(e) => updateOfferField(offer.slot_number, 'notes', e.target.value)}
              onBlur={() => handleFieldBlur(offer)}
              className="form-field__input"
              rows={2}
              placeholder="Optional notes about this offer..."
            />
          </div>
        </div>
      ))}

      {offers.length === 0 && (
        <EmptyState title="No offers yet." body="When a college extends an offer, log it here so it shows up on your card." />
      )}
      {offers.length < 10 && (
        <EditorialButton variant="ghost" onClick={handleAddOffer}>
          + Add another offer
        </EditorialButton>
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
